/**
 * Djay Copy Registry — All Djay speech messages in Dutch.
 * Based on MASCOT_DJAY_SPEC.md §7.
 * DDD co-locatie: moved from components/mascot/ in s60.
 *
 * Personalization context (LearnerContext) enables Djay to reference
 * the learner's actual progress, making messages feel alive.
 */

import type { DjayEmotion } from './DjayAvatar';
export interface DjayMessage {
  text: string;
  emotion: DjayEmotion;
}

/** Context passed to personalized message functions */
export interface LearnerContext {
  displayName: string;
  streak: number;
  level: number;
  xp: number;
  masteredCount: number;
  totalQuestionsAnswered: number;
  /** Day of week 0=Sun..6=Sat */
  dayOfWeek: number;
}
function randomPick<T>(items: T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}

// ── Greetings ────────────────────────────────────────────────

export function getDjayGreeting(displayName: string): DjayMessage {
  const hour = new Date().getHours();
  if (hour < 8) return {
    text: `*gaaap* Goeiemorgen, ${displayName}... ☀️`,
    emotion: 'sleeping'
  };
  if (hour < 12) return {
    text: `Goeiemorgen, ${displayName}! Klaar om te leren? 🚀`,
    emotion: 'happy'
  };
  if (hour < 18) return {
    text: `Hé ${displayName}! Fijn dat je er bent 🙌`,
    emotion: 'excited'
  };
  return {
    text: `Goedenavond, ${displayName}! 🌙`,
    emotion: 'happy'
  };
}

/**
 * Personalized greeting that takes full learner context into account.
 * Used by FloatingDjay on first load.
 */
export function getDjayPersonalGreeting(ctx: LearnerContext): DjayMessage {
  const {
    displayName,
    streak,
    level,
    xp,
    masteredCount,
    totalQuestionsAnswered,
    dayOfWeek
  } = ctx;
  const hour = new Date().getHours();

  // Streak milestones take priority
  if (streak >= 14) {
    return {
      text: `${displayName}, ${streak} dagen streak?! Je bent een legende! 👑🔥`,
      emotion: 'celebrating'
    };
  }
  if (streak >= 7) {
    return {
      text: `Wow ${displayName}, een hele week streak! Knap hoor! 🌟`,
      emotion: 'celebrating'
    };
  }
  if (streak >= 3) {
    return {
      text: randomPick([`Hé ${displayName}! ${streak} dagen op rij, lekker bezig! 🔥`, `${displayName}! Streak van ${streak} — ga zo door! 💪`]),
      emotion: 'excited'
    };
  }

  // Level milestones
  if (level >= 15) {
    return {
      text: randomPick([`Hé meester ${displayName}! Level ${level} al, wauw! 👑`, `${displayName}! Level ${level} — je bent echt een pro! 🌟`]),
      emotion: 'proud'
    };
  }
  if (level >= 10) {
    return {
      text: `Hé ${displayName}! Level ${level} — je groeit zo snel! 🚀`,
      emotion: 'excited'
    };
  }

  // Mastery milestones
  if (masteredCount >= 5) {
    return {
      text: `${displayName}! Al ${masteredCount} vakken onder de knie — indrukwekkend! 🏆`,
      emotion: 'proud'
    };
  }

  // Weekend fun
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    return {
      text: randomPick([`Hé ${displayName}! Weekend en toch aan het leren? Respect! 💪`, `${displayName}! Zelfs in het weekend bezig — top! 🌟`]),
      emotion: 'excited'
    };
  }

  // Time-based with personal touch
  if (hour < 8) {
    return {
      text: `*gaaap* Vroege vogel, ${displayName}... ☀️`,
      emotion: 'sleeping'
    };
  }
  if (hour >= 20) {
    return {
      text: `Goedenavond, ${displayName}! Nog even oefenen voor het slapengaan? 🌙`,
      emotion: 'happy'
    };
  }

  // XP milestone approaching
  if (xp > 0 && xp % 1000 >= 800) {
    const nextThousand = Math.ceil(xp / 1000) * 1000;
    return {
      text: `Hé ${displayName}! Nog ${nextThousand - xp} XP tot ${nextThousand}! Ga je het halen? 🎯`,
      emotion: 'pointing'
    };
  }

  // First-timer
  if (totalQuestionsAnswered === 0) {
    return {
      text: `Welkom, ${displayName}! Ik ben Djay, jouw leermaatje! Zullen we beginnen? 🚀`,
      emotion: 'excited'
    };
  }

  // Default personal greeting
  return getDjayGreeting(displayName);
}

// ── Answer Reactions ─────────────────────────────────────────

export function getDjayCorrectReaction(streak: number): DjayMessage {
  if (streak >= 10) {
    return {
      text: randomPick(['WAUW! Je bent niet te stoppen! 🔥🔥', 'Ongelooflijk! Wat een reeks! 🏆', 'Je bent een echte meester! 👑']),
      emotion: 'celebrating'
    };
  }
  if (streak >= 5) {
    return {
      text: randomPick(['Niet te stoppen! 🔥', 'Je bent on fire! ⚡', 'WAT?! Alweer goed! 🤯', 'Geweldig bezig! 💪']),
      emotion: 'celebrating'
    };
  }
  return {
    text: randomPick(['Yes! 🎯', 'Helemaal goed! ⭐', 'Jij snapt het! 💪', 'Top! 🙌', 'Netjes! ✨', 'Goed zo! 👏', 'Precies! 🎉']),
    emotion: 'excited'
  };
}
export function getDjayIncorrectReaction(): DjayMessage {
  return {
    text: randomPick(['Bijna! Kijk even naar de uitleg 👇', 'Niet erg! Hiervan leer je 📚', 'Goede poging! Kijk even mee 🔍', 'Hmm, net niet. Laten we kijken! 💡', 'Geen zorgen, hier word je beter van! 💪', 'Oeps! Maar fouten maken mag 🤗']),
    emotion: 'encouraging'
  };
}
export function getDjayHintPrompt(): DjayMessage {
  return {
    text: randomPick(['Psst... wil je even spieken? 🫣', 'Hmm, lastige... Zal ik helpen? 🤔', 'Ik heb een spiekbriefje voor je! 📝', 'Wil je een hint? 💡']),
    emotion: 'pointing'
  };
}

// ── Summary & Level Up ───────────────────────────────────────

export function getDjaySummaryReaction(percentage: number): DjayMessage {
  if (percentage === 100) return {
    text: 'Waanzinnig! Alles goed! 🏆',
    emotion: 'celebrating'
  };
  if (percentage >= 90) return {
    text: 'Fantastisch! Bijna alles goed! 🌟',
    emotion: 'celebrating'
  };
  if (percentage >= 80) return {
    text: 'Super gedaan! 🌟',
    emotion: 'celebrating'
  };
  if (percentage >= 60) return {
    text: 'Lekker geoefend! 💪',
    emotion: 'excited'
  };
  if (percentage >= 40) return {
    text: 'Goed bezig! Elke keer word je beter! 💪',
    emotion: 'encouraging'
  };
  return {
    text: 'Oefening baart kunst! Ga zo door! 📚',
    emotion: 'encouraging'
  };
}
export function getDjayLevelUpReaction(level: number): DjayMessage {
  if (level >= 10) return {
    text: `WOW! Level ${level}! Je bent een superster! 🌟👑`,
    emotion: 'celebrating'
  };
  if (level >= 5) return {
    text: `JAAAA! Level ${level}! Je groeit zo snel! 🚀`,
    emotion: 'celebrating'
  };
  return {
    text: `Yes! Level ${level}! 🎉`,
    emotion: 'celebrating'
  };
}
export function getDjayReturnGreeting(displayName: string): DjayMessage {
  return {
    text: randomPick([`Hé ${displayName}, welkom terug! 💙`, `${displayName}! Fijn je weer te zien! 🤗`, `Daar ben je weer, ${displayName}! Laten we oefenen! 🚀`]),
    emotion: 'happy'
  };
}

// ── Cheatsheet ───────────────────────────────────────────────

export function getDjayCheatsheetIntro(): DjayMessage {
  return {
    text: 'Hier, dit helpt je!',
    emotion: 'pointing'
  };
}
export function getDjayCheatsheetFallback(): DjayMessage {
  return {
    text: 'Sorry, hier heb ik nog geen spiekbriefje voor! 🤔',
    emotion: 'thinking'
  };
}

// ── Daily Goal & Tips ────────────────────────────────────────

export function getDjayDailyGoalReaction(): DjayMessage {
  return {
    text: randomPick(['BOOOM! Doel gehaald! 🎉', 'Yes! Je dagelijkse doel is bereikt! 🏆', 'Geweldig! Missie volbracht! ⭐']),
    emotion: 'celebrating'
  };
}
export function getDjayDailyTip(): DjayMessage {
  const tips: DjayMessage[] = [{
    text: 'Wist je dat? Door elke dag 10 minuutjes te oefenen, leer je veel sneller dan één keer per week een uur! 📚',
    emotion: 'pointing'
  }, {
    text: 'Tip: Fouten maken is super! Je hersenen leren het meest van fouten. 🧠',
    emotion: 'pointing'
  }, {
    text: 'Wist je dat? Als je iets hardop uitspreekt, onthoud je het beter! 🗣️',
    emotion: 'happy'
  }, {
    text: 'Tip: Neem tussendoor even pauze. Dan werken je hersenen op volle kracht! 💪',
    emotion: 'pointing'
  }, {
    text: 'Wist je dat? Je leert beter als je uitgerust bent. Slaap lekker! 😴',
    emotion: 'happy'
  }, {
    text: 'Tip: Probeer een moeilijke som eerst zelf, voordat je naar de uitleg kijkt! 🤔',
    emotion: 'thinking'
  }, {
    text: 'Wist je dat? Bewegen helpt je hersenen! Doe tussendoor even een dansje! 💃',
    emotion: 'excited'
  }, {
    text: 'Tip: Leg aan iemand uit wat je hebt geleerd. Zo onthoud je het nog beter! 🎓',
    emotion: 'pointing'
  }, {
    text: 'Wist je dat? Je hersenen maken nieuwe verbindingen terwijl je slaapt! 🌙',
    emotion: 'happy'
  }, {
    text: 'Tip: Begin met iets makkelijks om warm te draaien, dan lukt het moeilijke ook! 🔥',
    emotion: 'pointing'
  }];
  return randomPick(tips);
}
export function getDjayThinkingMessage(): DjayMessage {
  return {
    text: randomPick(['Hmm, even denken... 🤔', 'Neem je tijd! ⏰', 'Rustig aan, je kunt het! 💪']),
    emotion: 'thinking'
  };
}
export function getDjayFirstTimeSubject(subjectName: string): DjayMessage {
  return {
    text: `Leuk! Je eerste keer ${subjectName}! Laten we beginnen! 🚀`,
    emotion: 'excited'
  };
}

// ── Mission & Lab ────────────────────────────────────────────

export function getDjayMissionHeading(): string {
  return randomPick(['Ik heb een speciale missie voor je! 🎯', 'Klaar voor de volgende uitdaging? 🚀', 'Jouw persoonlijke trainingsplan! 💪', 'Wat ga je vandaag leren? ✨', 'Ik heb iets leuks voor je! 🎉', 'Tijd om te trainen! Hier is je plan 🔬']);
}
export function getDjayLabWelcome(lastSession?: {
  modeId: string;
  familyId?: string;
  timestamp: number;
} | null): DjayMessage {
  if (lastSession) {
    const hoursSince = (Date.now() - lastSession.timestamp) / (1000 * 60 * 60);
    const daysSince = hoursSince / 24;
    if (hoursSince < 24) {
      const modeName = lastSession.modeId === 'fluency' ? 'Snel & Zeker' : lastSession.modeId === 'sprint' ? 'een Sprint' : lastSession.modeId === 'challenge' ? 'een Uitdaging' : lastSession.modeId === 'free' ? 'Vrij Oefenen' : 'een oefening';
      return {
        text: `Welkom terug! Gisteren deed je ${modeName} — klaar voor meer? 💪`,
        emotion: 'excited' as DjayEmotion
      };
    } else if (daysSince >= 2 && daysSince <= 7) {
      return {
        text: 'Hé, lang niet gezien! Laten we weer oefenen! 🚀',
        emotion: 'happy' as DjayEmotion
      };
    } else if (daysSince > 7) {
      return {
        text: 'Welkom terug in het lab! Tijd om het stof eraf te blazen! 🧪',
        emotion: 'happy' as DjayEmotion
      };
    }
  }
  return randomPick([{
    text: 'Welkom in het Trainingslab! Wat ga je oefenen? 🔬',
    emotion: 'excited' as DjayEmotion
  }, {
    text: 'Het lab staat klaar! Kies je training 💪',
    emotion: 'happy' as DjayEmotion
  }, {
    text: 'Hé! Goed dat je er bent. Laten we trainen! 🚀',
    emotion: 'excited' as DjayEmotion
  }, {
    text: 'Klaar voor een trainingssessie? Ik heb een plan! 🎯',
    emotion: 'pointing' as DjayEmotion
  }, {
    text: 'Trainingstijd! Wat wordt het vandaag? 🏋️',
    emotion: 'happy' as DjayEmotion
  }]);
}
export function getDjayModeIntro(modeId: string): DjayMessage {
  const intros: Record<string, DjayMessage> = {
    fluency: {
      text: 'Vloeiend oefenen — herhaling is de sleutel! 🔄',
      emotion: 'happy'
    },
    sprint: {
      text: 'Focus Sprint! Concentreer je op één onderwerp 🎯',
      emotion: 'pointing'
    },
    challenge: {
      text: 'Uitdagingsmodus! Dit wordt spannend 💪',
      emotion: 'excited'
    },
    experiments: {
      text: 'Ontdek nieuwe vragen en interactieve opdrachten! 🔬',
      emotion: 'excited'
    },
    assessment: {
      text: 'Oefentoets tijd! Laat zien wat je kunt 🏆',
      emotion: 'excited'
    }
  };
  return intros[modeId] ?? {
    text: 'Laten we oefenen! 🚀',
    emotion: 'happy'
  };
}

// ── View Messages (Personalized) ─────────────────────────────

export function getDjayViewMessage(view: string, context: {
  streak: number;
  displayName: string;
}): DjayMessage {
  const {
    streak,
    displayName
  } = context;
  switch (view) {
    case 'home':
      if (streak >= 5) {
        return {
          text: `Wow, een streak van ${streak}! Ga zo door, ${displayName}! 🔥`,
          emotion: 'celebrating'
        };
      }
      return getDjayGreeting(displayName);
    case 'subjects':
    case 'modules':
      return {
        text: randomPick(['Wat zullen we vandaag gaan leren? 📚', `Kies een vak, ${displayName}! Wat spreekt je aan? 🎯`, 'Alle vakken staan voor je klaar! 📖']),
        emotion: 'thinking'
      };
    case 'progress':
      if (streak >= 3) {
        return {
          text: `Kijk ${displayName}, ${streak} dagen op rij! Ik ben trots op je! 🌟`,
          emotion: 'proud'
        };
      }
      return {
        text: randomPick([`Kijk hoe ver je al bent, ${displayName}! 🌟`, 'Wow, wat een voortgang! Ik ben trots op je! 💪', `${displayName}, check je groei! 📈`]),
        emotion: 'proud'
      };
    case 'settings':
      return {
        text: 'Pas alles aan zoals jij wilt! ⚙️',
        emotion: 'happy'
      };
    case 'collection':
      return {
        text: randomPick([`Jouw collectie, ${displayName}! Bekijk je prestaties ✨`, 'Wow, kijk wat je allemaal hebt verzameld! 🏆', `${displayName}'s trofeeënkast! Mooi hè? 🌟`]),
        emotion: 'proud'
      };
    case 'lab':
    case 'learningLab':
      return {
        text: randomPick([`Welkom in het lab, ${displayName}! Kies je training 💪`, 'Het Trainingslab staat klaar! Wat wordt het? 🔬', `${displayName}, tijd om te trainen! 🏋️`]),
        emotion: 'excited'
      };
    case 'assessment':
      return {
        text: `Tijd voor een oefentoets, ${displayName}! Laat zien wat je kunt 🏆`,
        emotion: 'excited'
      };
    default:
      return getDjayGreeting(displayName);
  }
}

/**
 * Personalized idle message — shown after 2 min inactivity.
 * Uses learner context to give relevant nudges.
 */
export function getDjayIdleMessage(ctx: LearnerContext): DjayMessage {
  const {
    displayName,
    streak,
    level,
    totalQuestionsAnswered
  } = ctx;
  if (streak >= 1 && totalQuestionsAnswered > 0) {
    return {
      text: randomPick([`${displayName}, je streak is ${streak} dagen! Nog even doorgaan? 🔥`, `Hé ${displayName}, zullen we verder oefenen? Je bent lekker bezig! 💪`, `${displayName}? Ik mis je al! Laten we nog even oefenen 🤗`]),
      emotion: 'encouraging'
    };
  }
  if (totalQuestionsAnswered === 0) {
    return {
      text: `${displayName}, zullen we je eerste oefening proberen? Het is leuk, beloofd! 🚀`,
      emotion: 'pointing'
    };
  }
  return {
    text: randomPick([`${displayName}? Zullen we verder gaan? 🤔`, `Hé ${displayName}, nog even oefenen? 💪`, 'Ik ben er nog hoor! Klaar als jij klaar bent 😊']),
    emotion: 'thinking'
  };
}

// ── Splash & Feedback ────────────────────────────────────────

export function getDjaySplashMessage(isCorrect: boolean, streak: number): {
  main: string;
  sub: string | null;
  emotion: DjayEmotion;
} {
  if (isCorrect) {
    if (streak >= 5) {
      return {
        main: randomPick(['Je bent on fire! 🔥', 'Niet te stoppen! ⚡', 'Wat een reeks! 🌟']),
        sub: null,
        emotion: 'celebrating'
      };
    }
    return {
      main: randomPick(['Helemaal goed! 🌟', 'Top! Je snapt het! ✨', 'Netjes! 🎯', 'Super! 💪', 'Precies goed! ⭐', 'Lekker bezig! 🚀']),
      sub: null,
      emotion: 'excited'
    };
  }
  return {
    main: randomPick(['Bijna!', 'Nog niet helemaal', 'Hmm, net niet']),
    sub: randomPick(['Geen zorgen, hier leer je van! 💪', 'Laten we samen kijken 🔍', 'Goede poging! Kijk even mee 📚']),
    emotion: 'encouraging'
  };
}
export function getDjayDistractorIntro(): string {
  return randomPick(['Hmm, snap ik! Maar kijk eens...', 'Goede gedachte! Let even op dit:', 'Goede gedachte, maar let op:', 'Bijna! Het verschil zit hem hier:']);
}
export function getDjayExplanationIntro(): string {
  return randomPick(['Zo werkt het:', 'Kijk, het zit zo:', 'Dit is de truc:', 'Zo pak je het aan:']);
}

// ── Onboarding & Registration ────────────────────────────────

export function getDjayWelcomeMessage(): DjayMessage {
  return {
    text: 'Hé! Ik ben Djay, jouw leermaatje! 👋',
    emotion: 'excited'
  };
}
export function getDjayRegistrationStep(step: string, name?: string, role?: string): DjayMessage {
  switch (step) {
    case 'role':
      return {
        text: 'Hoi! Leuk dat je er bent. Wie ben jij?',
        emotion: 'excited'
      };
    case 'name':
      return {
        text: 'Leuk je te ontmoeten! Hoe heet je?',
        emotion: 'happy'
      };
    case 'details':
      if (role === 'learner') return {
        text: `In welke groep zit je, ${name || 'vriend'}?`,
        emotion: 'thinking'
      };
      if (role === 'teacher') return {
        text: 'Op welke school geef je les?',
        emotion: 'pointing'
      };
      return {
        text: 'Wat is je e-mailadres?',
        emotion: 'pointing'
      };
    case 'password':
      return {
        text: 'Bijna klaar! Kies een sterk wachtwoord.',
        emotion: 'encouraging'
      };
    case 'success':
      return {
        text: 'Gelukt! Laten we beginnen! 🎉',
        emotion: 'celebrating'
      };
    default:
      return {
        text: 'Hoi!',
        emotion: 'happy'
      };
  }
}
export function getDjayTeacherOnboardingStep(step: number): DjayMessage {
  switch (step) {
    case 1:
      return {
        text: 'Welkom! Laten we je klas opzetten. Hoe heet je klas?',
        emotion: 'happy'
      };
    case 2:
      return {
        text: 'Aan welke groep geef je les?',
        emotion: 'thinking'
      };
    case 3:
      return {
        text: 'Tijd om je leerlingen toe te voegen!',
        emotion: 'excited'
      };
    default:
      return {
        text: 'Hoi!',
        emotion: 'happy'
      };
  }
}

// ── Reaction Scheduling ──────────────────────────────────────

/** Variable ratio schedule — Djay reageert niet altijd (voorkomt irritatie) */
export function shouldDjayReact(trigger: string, streak: number): boolean {
  switch (trigger) {
    case 'answer-correct':
      // Always react at milestones (5, 10, 15...)
      if (streak >= 5 && streak % 5 === 0) return true;
      // 40% chance otherwise
      return Math.random() < 0.4;
    case 'answer-incorrect':
      // 60% chance — more frequent for scaffolding value
      return Math.random() < 0.6;
    case 'level-up':
    case 'daily-goal-reached':
    case 'tier-evolution':
    case 'session-complete':
      // Always react at milestones
      return true;
    case 'streak-milestone':
      return true;
    default:
      return true;
  }
}